import { useCallback } from "react";
import { useApi } from "../../hooks/useApi";
import { emailService } from "../../services/emails";
import { SendEmailResult } from "../../types/emailAccount.types";
import { useSession } from "../../context/SessionContext";
import { toAppError } from "../../lib/feedback";

export type SendEmailOutcome =
  | { status: "sent"; result: SendEmailResult }
  | { status: "not-connected" }
  | { status: "send-failed"; message?: string };

export function useSendEmail() {
  const { metadata } = useSession();
  const sendApi = useApi<SendEmailOutcome>();

  // 409 (conta não conectada) e 502 (falha real no envio, ex: token do Google
  // expirado) precisam de tratamento específico na tela — não são erros
  // genéricos — então são capturados aqui dentro, convertidos em um retorno
  // não-lançado, e só chegam ao toast de erro automático do `request` se
  // forem qualquer outra falha (404/422/500/rede).
  const sendEmail = useCallback(async (
    hospitalIds: number[],
    companyIds: number[],
    files: File[],
    subject: string,
    body: string,
  ) => {
    if (!metadata.email) return null;

    return sendApi.request(async () => {
      try {
        const result = await emailService.send(
          sendApi.apiFetchForm,
          metadata.email!,
          hospitalIds,
          companyIds,
          files,
          subject,
          body,
        );
        return { status: "sent", result };
      } catch (caught) {
        const err = toAppError(caught);
        // Pelo `type`, não pelo status: outro 409/502 (ex.: Stripe, banco) não é
        // "conta desconectada" nem "falha no envio".
        if (err?.type === "email-account-not-connected") return { status: "not-connected" };
        // `err.detail` já traz a mensagem certa do backend — genérica ou, se a conta
        // tinha um token sem permissão de envio salvo antes dessa correção existir,
        // a variante orientando a reconectar. Nunca comparar esse texto por igualdade.
        if (err?.type === "email-send-failed" || err?.type === "email-oauth-failed") {
          return { status: "send-failed", message: err?.detail };
        }
        throw caught;
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metadata.email, sendApi.apiFetchForm]);

  return { sendEmail, sending: sendApi.loading };
}
